import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ShopifyProduct, CartItem } from '../home/models';
import { DesignQueueService } from './design-queue.service';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private shopifyProducts: Map<string, { product: ShopifyProduct; quantity: number }> = new Map();
  private shopifyProductsSubject = new BehaviorSubject<Map<string, { product: ShopifyProduct; quantity: number }>>(new Map());
  
  shopifyProducts$: Observable<Map<string, { product: ShopifyProduct; quantity: number }>> = this.shopifyProductsSubject.asObservable();

  constructor(private designQueueService: DesignQueueService) {}

  /**
   * Add a Shopify product to cart
   */
  addShopifyProduct(product: ShopifyProduct, quantity: number = 1): void {
    const existing = this.shopifyProducts.get(product.id);
    if (existing) {
      existing.quantity += quantity;
    } else {
      this.shopifyProducts.set(product.id, { product, quantity });
    }
    this.notifyShopifyProductsSubscribers();
  }

  /**
   * Remove a Shopify product from cart
   */
  removeShopifyProduct(productId: string): void {
    this.shopifyProducts.delete(productId);
    this.notifyShopifyProductsSubscribers();
  }

  /**
   * Update quantity of a Shopify product
   */
  updateShopifyProductQuantity(productId: string, quantity: number): void {
    const item = this.shopifyProducts.get(productId);
    if (item) {
      item.quantity = Math.max(1, quantity);
      this.notifyShopifyProductsSubscribers();
    }
  }

  /**
   * Get all Shopify products in cart
   */
  getShopifyProducts(): Array<{ product: ShopifyProduct; quantity: number }> {
    return Array.from(this.shopifyProducts.values());
  }

  /**
   * Get total quantity of Shopify products
   */
  getShopifyProductsQuantity(): number {
    return Array.from(this.shopifyProducts.values())
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Get total price of Shopify products
   */
  getShopifyProductsTotal(): number {
    return Array.from(this.shopifyProducts.values())
      .reduce((sum, item) => {
        const price = parseFloat(item.product.price.amount);
        return sum + (price * item.quantity);
      }, 0);
  }

  /**
   * Get combined total quantity (custom designs + Shopify products)
   */
  getTotalQuantity(): number {
    return this.designQueueService.getTotalQuantity() + this.getShopifyProductsQuantity();
  }

  /**
   * Get combined total price (custom designs + Shopify products)
   */
  getTotalPrice(customMagnetPricePerUnit: number): number {
    const customTotal = this.designQueueService.getTotalPrice(customMagnetPricePerUnit);
    const shopifyTotal = this.getShopifyProductsTotal();
    return customTotal + shopifyTotal;
  }

  /**
   * Check if cart is empty (both custom and Shopify)
   */
  isEmpty(): boolean {
    return this.designQueueService.isEmpty() && this.shopifyProducts.size === 0;
  }

  /**
   * Clear all Shopify products from cart
   */
  clearShopifyProducts(): void {
    this.shopifyProducts.clear();
    this.notifyShopifyProductsSubscribers();
  }

  /**
   * Clear entire cart (both custom and Shopify)
   */
  clearAll(): void {
    this.designQueueService.clear();
    this.clearShopifyProducts();
  }

  /**
   * Notify subscribers of Shopify products changes
   */
  private notifyShopifyProductsSubscribers(): void {
    this.shopifyProductsSubject.next(new Map(this.shopifyProducts));
  }
}
